import React from "react";
import styles from "./Button.module.css";

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

const VARIANTS = new Set(["primary", "secondary", "special"]);
const TONES = new Set(["blue", "green", "red", "neutral"]);
const SIZES = new Set(["md", "lg"]);

function toCssSize(v) {
  if (v == null) return undefined;
  if (typeof v === "number") return `${v}px`;
  return String(v);
}

function radiusFromHeight(height) {
  if (height == null) return undefined;
  const n =
    typeof height === "number"
      ? height
      : Number(String(height).replace("px", ""));
  if (!Number.isFinite(n)) return undefined;
  return `${Math.round(n / 2)}px`;
}

const Button = React.forwardRef(function Button(
  {
    children,
    className,
    style,
    labelStyle,

    variant = "primary",
    tone = "blue",

    size,
    width,
    height,
    fullWidth = false,

    icon = null,
    iconPosition = "left",

    iconLeft = null,
    iconRight = null,

    loading = false,
    disabled = false,

    type = "button",
    ...props
  },
  ref,
) {
  const resolvedVariant = VARIANTS.has(variant) ? variant : "primary";
  const resolvedTone = TONES.has(tone) ? tone : "blue";

  const inferredSize = resolvedVariant === "special" ? "lg" : "md";
  const resolvedSize = SIZES.has(size) ? size : inferredSize;

  const isDisabled = disabled || loading;

  const effectiveIconLeft = iconLeft ?? (iconPosition === "left" ? icon : null);
  const effectiveIconRight =
    iconRight ?? (iconPosition === "right" ? icon : null);
  const effectiveIconCenter = iconPosition === "center" ? icon : null;

  const rootStyle = {
    width: fullWidth ? "100%" : toCssSize(width),
    height: toCssSize(height),
    borderRadius: radiusFromHeight(height),
    ...style,
  };

  const isIconOnly = Boolean(effectiveIconCenter) && !children;

  return (
    <button
      {...props}
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={cx(
        styles.btn,
        styles[`variant_${resolvedVariant}`],
        styles[`tone_${resolvedTone}`],
        styles[`size_${resolvedSize}`],
        fullWidth ? styles.fullWidth : "",
        isDisabled ? styles.disabled : "",
        loading ? styles.loading : "",
        isIconOnly ? styles.iconOnly : "",
        className,
      )}
      style={rootStyle}
      aria-busy={loading ? "true" : undefined}
    >
      {effectiveIconLeft ? (
        <span className={styles.iconLeft}>{effectiveIconLeft}</span>
      ) : null}

      {effectiveIconCenter ? (
        <span className={styles.iconCenter}>{effectiveIconCenter}</span>
      ) : null}

      {children ? (
        <span className={styles.label} style={labelStyle}>
          {children}
        </span>
      ) : null}

      {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}

      {effectiveIconRight ? (
        <span className={styles.iconRight}>{effectiveIconRight}</span>
      ) : null}
    </button>
  );
});

export default Button;
